import { Loading } from '@perch-labs/shrike';
import { isError } from 'lodash';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchFunnelPacket, fetchUtilityConnectToken, updateOriginationSource } from '~/services';
import { EnrollmentDataSource } from '~/typings/context';
import { INTERNAL_ROUTES } from '~/utils/constants';
import { store } from '~/utils/store';
import { Credentials, CredentialsProps } from './credentials.component';

export type UserInfoProps = {
  verificationStatus: string;
  utility: any;
  utilityConnectToken: string | undefined;
  user: any;
  funnelId: string | undefined;
};

export const CredentialsWrapper = ({ onUtilityConnect }) => {
  const [error, setError] = useState<string>('');
  const [userInfo, setUserInfo] = useState<UserInfoProps>({} as UserInfoProps);
  const [loading, setLoading] = useState<boolean>(true);
  const userId = store.get('USER_ID')!;
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) {
      navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
        state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
      });
    }
  }, [userId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const funnelPacket = await fetchFunnelPacket({ userId });
        const data: UserInfoProps = {
          verificationStatus: funnelPacket.utilityCredential.verificationStatus,
          utility: funnelPacket.utility,
          utilityConnectToken: undefined,
          user: funnelPacket.user,
          funnelId: 'sponsored-solar',
        };

        try {
          const tokenResponse = await fetchUtilityConnectToken({
            utilityCredentialId: funnelPacket.utilityCredential.id,
          });
          data.utilityConnectToken = tokenResponse.utilityConnectToken;
        } catch (error) {
          setError(isError(error) && error.message ? error.message : 'Something went wrong');
        } finally {
          setUserInfo({ ...data });
          setLoading(false);
        }
      } catch (_error) {
        setLoading(false);
        navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
          state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
        });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (userInfo?.user && userInfo?.utility) {
      setLoading(false);
    }
  }, [userInfo]);

  const overrideCopy = context => {
    const utility = context?.utility?.displayName;
    return {
      title: `Connect your ${utility} utility account`,
      description:
        'Your utility account information is required for participation in the community solar program so that we can analyze your data and maximize your savings.',
      syncUtilityButton: 'Enter utility login credentials',
      descriptionTwo: '',
      disclaimer: '',
      invalidDescriptionTwo: '',
      invalidDescriptionThree: '',
      learnMoreText: '',
      skipButton: 'Continue without logging in',
      retryUtilityConnectButton: 'Retry utility connect',
    };
  };

  const props: CredentialsProps = {
    context: userInfo,
    error,
    onError: () => {
      setError(
        'We were unable to log into your utility account. Please try again or click "Continue without logging in" and connect your utility account later in the dashboard.',
      );
    },
    updateStore: value => {
      store.set('ENROLLMENT_DATA_SOURCE_NAME', value as EnrollmentDataSource);
      updateOriginationSource(store.get('USER_ID'), undefined, undefined, {
        enrollmentDataSourceName: value,
      });
    },
    overrideCopy,
    flags: {
      showFaqButton: true,
      allowUtilityReconnection: true,
      allowSkipOnUtilityConnectFailure: true,
      hideUtilityConnectInherentError: true,
      showStep: true,
    },
    next: () => {
      onUtilityConnect();
    },
  };

  if (loading) return <Loading />;

  return <Credentials {...props} />;
};
